<template>
  <div class="accountChangeName">
    <account-header />
    <v-divider class="accountChangeName_divider" />

    <section class="accountChangeName_content">
      <section class="accountChangeName_old-name">
        <h3>ユーザーアプリで表示される、お客様の名前を入力してください。</h3>
      </section>

      <h4 class="accountChangeName_item">
        <span> ユーザー名</span>
        <em>必須</em>
      </h4>
      <v-text-field
        ref="name"
        v-model="currentName"
        :error-messages="error"
        class="accountChangeName_container_input"
        color="dark"
        required
        aria-autocomplete="off"
        autocomplete="off"
        full-width
        outlined
        maxlength="21"
        :rules="nameRules"
        @blur="correctNameInput"
      />
    </section>
    <div class="accountChangeName_action">
      <v-btn
        class="accountChangeName_btn-submit"
        :disabled="!isButtonEnabled"
        :loading="submitLoading"
        @click="handleSubmit"
      >
        ユーザー名を保存する
      </v-btn>
    </div>

    <!-- Message dialog -->
    <clip-dialog-message
      :open="!!dialogMessageText"
      :message="dialogMessageText"
      @onClose="closeDialogMessage"
    />
    <!-- ----------------- -->
  </div>
</template>

<script>
import AccountHeader from './AccountHeader.vue';
import ClipDialogMessage from '@/components/dialogs/ClipDialogMessage.vue';
import { mapActions, mapState } from 'vuex';
import { updateUserAvatarName } from '@/services/ccs.user.servic';

export default {
  name: 'AccountChangeName',
  components: {
    AccountHeader,
    ClipDialogMessage,
  },
  data() {
    return {
      submitLoading: false,
      name: '',
      dialogMessageText: '',
      error: '',
      currentName: '',
    };
  },
  computed: {
    ...mapState({
      userInfo: (state) => state.common.userInfo,
      avatarName: (state) => state.settings.avatarName,
      currentAvatarId: (state) => state.settings.currentAvatarId,
    }),
    isButtonEnabled() {
      return (
        this.avatarName !== this.currentName &&
        this.currentName.length > 0 &&
        this.validateField() === true &&
        !this.submitLoading
      );
    },
    nameRules() {
      return [
        (v) => (v && v.length > 0) || 'ユーザー名は必要項目です',
        (v) =>
          (v && v.length <= 21) || 'ユーザー名は21文字以下で入力してください',
        (v) => this.isValidName(v) || '設定できない文字列が含まれています',
      ];
    },
  },
  watch: {
    dialogMessageText(val) {
      val || this.closeDialogMessage();
    },
    avatarName: {
      immediate: true,
      handler: function (val) {
        this.currentName = val;
      },
    },
  },
  mounted() {},
  methods: {
    /**
     * Validates a name to ensure it meets specified character requirements.
     *
     * This function checks if a name contains only allowed characters by using a regular expression that excludes
     * any characters within the Unicode General_Category=Other. The function also accommodates multi-byte
     * characters (like certain kanji) and allows for whitespace. It ensures that each character in the string is
     * valid by comparing the length of the array of matched characters to the name's length in UTF-16 code units.
     *
     * @param {string} name - The name to validate.
     * @returns {boolean} - Returns true if the name contains only valid characters and spaces; otherwise, false.
     *
     * Note:
     * - Supports extended kanji characters that may require two UTF-16 code units (e.g., "𠮷" or "𩸽").
     * - Spaces are allowed, enabling multi-part names (e.g., "山田 太郎").
     * - Edge cases involving extended characters are handled by matching the number of non-"Other" category characters
     *   against the length of the name, allowing multi-byte characters to pass.
     * Example: 佐藤, 田中, 山田, 𠮷田, 𩸽田, 山田 太郎, 佐藤 次郎
     */
    isValidName(name) {
      // validate name
      const matchedChars = name.match(/\P{General_Category=Other}/gu);
      return matchedChars && matchedChars.join('') === name;
    },
    correctNameInput() {
      this.currentName = this.currentName.trim();
      const validCharacters = this.currentName.match(
        /\P{General_Category=Other}/gu
      );
      this.currentName = validCharacters ? validCharacters.join('') : '';
      this.$nextTick(() => {
        this.validateField();
      });
    },
    validateField() {
      this.error = this.nameRules
        .map((rule) => rule(this.currentName))
        .filter((result) => result !== true);
      return this.error.length === 0;
    },
    async handleSubmit() {
      try {
        this.submitLoading = true;
        // call changing name API
        await updateUserAvatarName(this.currentName, this.currentAvatarId);
        this.setAvatarName(this.currentName);
        this.dialogMessageText = 'ユーザー名を保存しました。';
      } catch (error) {
        console.error(error);
      } finally {
        this.submitLoading = false;
      }
    },
    closeDialogMessage() {
      this.dialogMessageText = null;
    },
    ...mapActions('settings', ['setAvatarName']),
  },
};
</script>

<style lang="scss">
.accountChangeName {
  overflow: auto;
  height: 100%;

  &_title {
    font-size: 14px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    text-align: left;
    color: #4b4b4b;
    margin-top: 16px;
  }

  &_item {
    display: flex;
    justify-content: space-between;
    width: 500px;
    span,
    em {
      font-size: 14px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      text-align: left;
      color: #666;
      margin: 10px 0;
    }
    em {
      color: #eb545b;
    }
  }
  &_container {
    display: flex;
    flex-direction: column;
    &_item {
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      height: 16px;
      color: #333333;
      &--error {
        color: var(--v-error-base);
      }
    }
    &_input {
      margin-bottom: 0;
      margin-top: 0;
      padding-top: 0;
      width: 500px;
      &.v-input {
        font-size: 18px;
        .v-messages:not(.error--text) {
          .v-messages__message {
            font-size: 12px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.5;
            letter-spacing: normal;
            text-align: left;
            color: #989898;
          }
        }
      }
      .v-text-field__slot {
        padding-left: 0;
      }

      .v-input__slot {
        min-height: 40px !important;
        margin-bottom: 2px;
        .v-input__append-inner {
          margin-top: 9px;
        }
        .v-message__message {
          font-size: 12px;
          font-weight: normal;
          font-stretch: normal;
          font-style: normal;
          line-height: 1.5;
          letter-spacing: normal;
          text-align: left;
          color: #989898;
        }
      }
      input {
        line-height: 32px !important;
        color: var(--text-color) !important;
        font-size: 18px !important;
        font-weight: normal !important;
        font-stretch: normal !important;
        font-style: normal;
        letter-spacing: normal;
        text-align: left;
        &:-webkit-autofill,
        &:-webkit-autofill::first-line {
          font-family: 'Noto Sans JP', sans-serif;
          font-size: 18px;
          font-weight: 500;
        }
      }

      .v-text-field__details {
        padding: 0 !important;
        padding-top: 0px !important;
        margin-bottom: 0 !important;
      }
    }
  }

  &_input {
    margin-top: 4px;
    &-label {
      font-size: 14px;
      font-weight: 500;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: 0.7px;
      text-align: left;
      color: var(--text-color-2);
      margin-right: 20px;
    }
    &-code {
      &.v-text-field {
        max-width: 168px;
        font-size: 40px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: 2px;
        text-align: center;
        color: var(--text-color);
        &.theme--light.v-input input {
          max-width: 168px;
          max-height: 70px;
          text-align: center;
        }
      }
    }
  }
  &_divider {
    margin-top: 16px;
    &.theme--light.v-divider {
      border-color: #ccc;
    }
  }
  &_gap-header {
    height: 26px;
  }
  &_old-name {
    padding: 15px 0 5px;
    h2 {
      font-size: 14px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
    }
    h4 {
      font-size: 16px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      margin-top: 10px;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
    }
  }
  &_action {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    border-top: 1px solid #eee;
    margin-top: 5px;
    padding: 15px 0;
    h3 {
      font-size: 14px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
    }
  }
  &_btn {
    &-submit {
      &.theme--light.v-btn.v-btn--has-bg {
        height: 36px;
        border-radius: 5px;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
        background-color: #0075c2;
        font-size: 14px;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.14;
        letter-spacing: normal;
        text-align: left;
        color: #fff;
      }
      &.theme--light.v-btn.v-btn--disabled.v-btn--has-bg {
        background-color: #f3f3f3 !important;
        box-shadow: none;
        color: #ccc !important;
      }
    }
  }
}
</style>
